import React from "react";
import { useQuery } from "react-query-legacy";
import { BillingEngineService } from "services";
import { City, isBillingEngineRequestError } from "services/BillingEngineService/interfaces";

type CityByIdMap = Record<string, City>;

interface Props {
  cities: City[];
  cityById: CityByIdMap;
  status: string;
}

const RateCardCitiesContext = React.createContext<Props | undefined>(undefined);

const RateCardCitiesProvider: React.FC = ({ children }) => {
  const { data, status } = useQuery("rate-card-cities", BillingEngineService.getCities, {
    refetchOnWindowFocus: false,
    cacheTime: 60, // cache for an hour at least
  });

  const cityById = React.useMemo(() => {
    if (!isBillingEngineRequestError(data) && data) {
      return data.reduce((prev, city) => {
        prev[city.id] = city;
        return prev;
      }, {} as CityByIdMap);
    }
    return {};
  }, [data]);

  return (
    <RateCardCitiesContext.Provider
      value={{
        cities: data as City[],
        cityById: cityById,
        status: status,
      }}
    >
      {children}
    </RateCardCitiesContext.Provider>
  );
};

/**
 * Hook to get access to OrganizationRateCardProvider
 */
const useRateCardCitiesContext = () => {
  const context = React.useContext(RateCardCitiesContext);

  if (!context) {
    throw new Error("useRateCardCitiesContext can be used only inside <RateCardCitiesProvider />");
  }

  return context;
};

export { RateCardCitiesProvider, useRateCardCitiesContext };
